import React from 'react';
import { useParams } from "react-router";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";
import DeleteSectionButton from './../DeleteSectionButton';
import LastUpdatedAt from './../LastUpdatedAt';
import { renderRailsErrors } from '../../../utils/rails_error_parser';
import {
  TextInput,
  RichTextInput,
  SelectInput,
  Base64FileInput,
  HookFormDirtyCheck,
} from "../../react-hook-form-inputs";
import { 
  usePitchableCustomersList,
  addCustomer,
  updateCustomer,
  deleteCustomer,
} from "./../queries";

import "./Customers.scss";
import { Loaders } from '../..';

export default function CustomerForm({ 
  pitch, pitchableType, pitchableId, updatePitchMutation
 }) {
  const { offer_id: pitchable_slug } = useParams()

  return (
    <div id="customerForm">
      <div className="col-xs-12 row">
        <ArticleContent pitch={pitch} pitchableId={pitchableId} pitchableType={pitchableType} updatePitchMutation={updatePitchMutation} />
        <Customers pitchable_slug={pitchable_slug} pitchable_id={pitch.pitchable_id} pitchableType={pitchableType} pitchableId={pitchableId} />
      </div>
    </div>
  );
}

const CustomerArticleSchema = Yup.object().shape({
  content: Yup.object()
    .test(
      'has text',
      'Article content is required',
      value => value && value.getCurrentContent().getPlainText().replace(/\s/g, '').length > 0
    ),
});

const ArticleContent = ({ 
  pitch:{customers_section={}}, pitchableType, pitchableId, updatePitchMutation
}) => {
  if(customers_section === null) customers_section = {};

  const defaultValues = {
    content: RichTextInput.createState(customers_section.content)
  }

  const methods = useForm({
    resolver: yupResolver(CustomerArticleSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: defaultValues
  });

  React.useEffect(() => {
    methods.setValue("content", RichTextInput.createState(customers_section.content || ""));
  }, [methods, customers_section]);

  const onSubmit = values => {
    const { content} = values
    updatePitchMutation.mutate({
      pitchable_type: pitchableType,
      pitchable_id: pitchableId,
      values: {
        pitch: {
          customers_section_attributes: {
            content: RichTextInput.contentForSave(content),
          }
        }
      }
    })
  }

  return (
    <div className="col-lg-7">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-1">
          <div>
            <label className="mb-1">Article content</label>
            <RichTextInput name="content" hideExternalPlugins={false} containerStyle={{ height: "400px"}}/>
          </div>
          <div className="form-group mt-3">
            <button type="submit"
              className="mx-auto btn btn-outline-dark rounded-0"
              disabled={methods.isSubmitting}
            >
              Save Section
            </button>
            <DeleteSectionButton pitchableType={pitchableType} pitchableId={pitchableId} sectionId={customers_section.id} sectionType="customers"/>
            <LastUpdatedAt updatedAt={ customers_section.updated_at }/>
            <HookFormDirtyCheck />
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

const CustomerView = (props) => {
  const { customer } = props
  return (
    <div className="customer-view">
      <img src={customer.logo_url} alt="" className="logo" />
      <div className="customer-summary">
        <div className="customer-name">{customer.name}</div>
        <div className="customer-status">{customer.status}</div>
      </div>
      <div className="actions pr-2">
        <div className="edit" onClick={props.onEdit}></div>
        <div className="remove" onClick={props.onRemove}></div>
      </div>
    </div>
  )
}

const ResetCustomerFormButton = (props) => {
  return (
    <button type="button" className="btn btn-outline-warning rounded-0 ms-3" onClick={props.onClick}>
      {props.text}
    </button>
  )
}
const CustomerSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required'),
  status: Yup.string().required('Status is required'),
  logo: Yup.mixed().required('Logo is required')
})

const Customers = ({
  pitchable_id, pitchable_slug, pitchableType, pitchableId
}) => {
  const queryClient = useQueryClient();
  
  // const {data:customers=[], isLoading, isIdle} = usePitchableCustomersList({pitchable_type: pitchableType, pitchable_id: pitchable_slug})
  const {data:customers=[], isLoading, isIdle} = usePitchableCustomersList({pitchable_type: pitchableType, pitchable_id: pitchableId})
  const customerFormBlankState = { name: "", status: "", logo: "" }
  const [customerforEdit, setCustomerForEdit] = React.useState(null)

  const addCustomerMutation = useMutation(
    addCustomer,
    {
      onSuccess: () => {
        toast.success('Customer added.')
        queryClient.invalidateQueries(
          // ['customers-list', pitchableType, pitchable_slug],
          ['customers-list', pitchableType, pitchableId],
          {
            exact: true
          }
        )
        setCustomerForEdit(customerFormBlankState);
      },
      onError: (error) => {
        toast.error('Customer add failed.')
        renderRailsErrors(error)
      },
    }
  )

  const updateCustomerMutation = useMutation(
    updateCustomer,
    {
      onSuccess: () => {
        toast.success('Customer updated.')
        queryClient.invalidateQueries(
          // ['customers-list', pitchableType, pitchable_slug],
          ['customers-list', pitchableType, pitchableId],
          {
            exact: true
          }
        )
        setCustomerForEdit(customerFormBlankState);
      },
      onError: (error) => {
        toast.error('Customer update failed.')
        renderRailsErrors(error)
      },
    }
  )

  const deleteCustomerMutation = useMutation(
    deleteCustomer,
    {
      onSuccess: () => {
        toast.success('Customer deleted.')
        queryClient.invalidateQueries(
          // ['customers-list', pitchableType, pitchable_slug],
          ['customers-list', pitchableType, pitchableId],
          {
            exact: true
          }
        )
        setCustomerForEdit(customerFormBlankState);
        // setFile(null);
      },
      onError: (error) => {
        toast.error('Customer delete failed.')
        renderRailsErrors(error)
      },
    }
  )

  const defaultValues = {
    name: "",
    status: "",
    logo: "",
  }
  const methods = useForm({
    resolver: yupResolver(CustomerSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: defaultValues
  });

  React.useEffect(() => {
    methods.setValue("name", customerforEdit?.name || "");
    methods.setValue("status", customerforEdit?.status || "");
  }, [methods, customerforEdit]);

  const onSubmit = values => {
    if (customerforEdit) {
      updateCustomerMutation.mutate({
        customer_id: customerforEdit.id,
        values: {
          customer: values
        }
      })
    }
    else {
      addCustomerMutation.mutate({
        pitchable_type: pitchableType,
        pitchable_id: pitchableId,
        values: {
          customer: values
        }
      })
    }
  }

  const showResetButton = 
    customerforEdit !== null
    || methods.watch(["name", "status", "logo"]).filter(Boolean).length > 0 
    || methods.formState.isDirty
    || Object.keys(methods.formState.errors).length > 0;

  if (isLoading || isIdle)
    return <Loaders.SpacedPageLoader />

  return (
    <div className="col-lg-5">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="mt-1">
          <label className="mb-1">Add Customers</label>
          <div className="col-xs-12 row">
            <div>
              <TextInput
                name="name"
                placeholder="Customer name"
              />
            </div>
            <div>
            <SelectInput
                name="status"
                placeholder="Status"
              >
                <option value="">Select Status</option>
                <option value="discovery">Discovery</option>
                <option value="letter_of_intent">Letter of Intent</option>
                <option value="pilot">Pilot</option>
                <option value="sales">Sales</option>
                <option value="recurring">Recurring</option>
              </SelectInput>
            </div>
            <div className="my-2">
              <Base64FileInput
                name="logo"
                prevUrl={customerforEdit?.logo_url}
                containerSizingClass="customer-logo"
                prompt="Customer logo"
              />
            </div>
          </div>
          <div className="form-group mt-3">
            <button type="submit"
              className="mx-auto btn btn-outline-dark rounded-0"
              disabled={methods.isSubmitting}
            >
              {customerforEdit ? "Update Customer" : "Add Customer"}
            </button>
            { showResetButton && <ResetCustomerFormButton 
              text={ customerforEdit ? 'cancel edit' : 'clear form'}
              onClick={() => {
                setCustomerForEdit(null);
                methods.reset();
              }}
            />}
            <HookFormDirtyCheck />
          </div>
          <hr />
          <div className="customers-list">
              {customers && customers.map((c) =>
                <CustomerView
                  key={c.id}
                  customer={c}
                  onEdit={() => { setCustomerForEdit(c) }}
                  onRemove={() => {
                    if (window.confirm(`Are sure you want to delete customer - ${c.name}`)) { 
                      deleteCustomerMutation.mutate({
                        customer_id: c.id,
                      })
                    }
                  }}
                />
              )}
            </div>
        </form>
      </FormProvider>
    </div>
  )
}
